import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { CoreDrawer } from "@/components/common/CoreDrawer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQRGenerator } from "@/hooks/embed/useQRGenerator";
import { useQRCodeRedownload } from "@/hooks/qr-code/useSavedQRCodes";
import { useTeamData } from "@/hooks/users/useTeamData";
import { Agent } from "@/types/agent/agent";
import { DEFAULT_QR_DISPLAY_CONFIG } from "@/types/ui/branding";
import { buildGenieKeepUrl, buildGenieTalkUrl } from "@/utils/agent/genieShareLinks";
import ManageAccessSection from "./share/ManageAccessSection";
import PhoneAccessSection from "./share/PhoneAccessSection";
import ShareOverviewSection from "./share/ShareOverviewSection";
import SlackAccessSection from "./share/SlackAccessSection";
import TeamsAccessSection from "./share/TeamsAccessSection";

interface GenieShareDrawerProps {
  genie: Agent;
  isOpen: boolean;
  onClose: () => void;
  onOpenPhoneModal?: () => void;
}

type ShareTab = "overview" | "access" | "phone" | "slack" | "teams";

export default function GenieShareDrawer({ genie, isOpen, onClose, onOpenPhoneModal }: GenieShareDrawerProps) {
  const navigate = useNavigate();
  const { user, team } = useAuth();
  const { data: teamData } = useTeamData(team?.id, user?.id);
  const [tab, setTab] = useState<ShareTab>("overview");
  const shareUrl = `${window.location.origin}/live-agent/${genie.url_name ?? genie.page?.url_name}`;

  const genieUrlName = genie.url_name ?? genie.page?.url_name;
  // Two links, two jobs — see utils/agent/genieShareLinks.
  const talkUrl = buildGenieTalkUrl(genieUrlName, { receiveIntent: true }) ?? shareUrl;
  const keepUrl = buildGenieKeepUrl({
    urlName: genieUrlName,
    agentId: genie.id,
    name: genie.name,
  });
  const qrValue = genieUrlName ? `${window.location.origin}/live-agent/${genieUrlName}?ref=qr` : "";
  const { downloadQR } = useQRGenerator(qrValue, genie, { size: 400 });
  const redownloadMutation = useQRCodeRedownload();

  const handleClose = () => {
    setTab("overview");
    onClose();
  };

  const handleGoToActivation = () => {
    onClose();
    navigate(`/genie-hub/activation/genie/${genie.id}#activate-app`);
  };

  const handleDownloadQR = async () => {
    const savedCodes = genie.saved_qr_codes ?? [];
    const mostRecent = [...savedCodes].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )[0];

    try {
      if (mostRecent) {
        await redownloadMutation.mutateAsync(mostRecent);
      } else {
        if (!qrValue) {
          toast.error("This genie needs to be fully set up before a QR code can be generated");
          return;
        }
        await downloadQR("png", {
          ...DEFAULT_QR_DISPLAY_CONFIG,
          foregroundColor: genie.branding?.qrCodeStyle?.foregroundColor || "#000000",
          backgroundColor: genie.branding?.qrCodeStyle?.backgroundColor || "#FFFFFF",
        });
      }
    } catch (error) {
      console.error("Error downloading QR code:", error);
      toast.error("Failed to download QR code");
    }
  };

  const handleConfigurePhone = () => {
    if (onOpenPhoneModal) {
      onClose();
      onOpenPhoneModal();
    } else {
      onClose();
      navigate(`/genie-hub/my-genies/${genie.id}#distribute`);
    }
  };

  const handleGoToMailbox = () => {
    onClose();
    navigate(`/genie-hub/my-genies/${genie.id}#overview`);
  };

  return (
    <CoreDrawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Share Genie"
      description="Control who can access this genie and how people find it."
      className="sm:!max-w-[50vw]"
    >
      <Tabs value={tab} onValueChange={(value) => setTab(value as ShareTab)}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="access">
            <span className="sm:hidden">Access</span>
            <span className="hidden sm:inline">Manage Access</span>
          </TabsTrigger>
          <TabsTrigger value="phone">Phone</TabsTrigger>
          <TabsTrigger value="slack">Slack</TabsTrigger>
          <TabsTrigger value="teams">Teams</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <ShareOverviewSection
            genie={genie}
            shareUrl={talkUrl}
            keepUrl={keepUrl}
            onGoToManageAccess={() => setTab("access")}
            onGoToPhone={() => setTab("phone")}
            onGoToMailbox={handleGoToMailbox}
            onGoToSlack={() => setTab("slack")}
            onGoToTeams={() => setTab("teams")}
            onGoToActivation={handleGoToActivation}
            onDownloadQR={handleDownloadQR}
            onClose={onClose}
          />
        </TabsContent>

        <TabsContent value="access">
          <ManageAccessSection
            genie={genie}
            team={teamData}
            shareUrl={shareUrl}
            userId={user?.id || ""}
            onClose={handleClose}
          />
        </TabsContent>

        <TabsContent value="phone">
          <PhoneAccessSection genie={genie} onConfigurePhone={handleConfigurePhone} />
        </TabsContent>

        <TabsContent value="slack">
          <SlackAccessSection genie={genie} />
        </TabsContent>

        <TabsContent value="teams">
          <TeamsAccessSection genie={genie} />
        </TabsContent>
      </Tabs>
    </CoreDrawer>
  );
}
